import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

// ============================================================================
// Types
// ============================================================================

export type ScannedDocumentStatus =
  | 'detected'
  | 'parsing'
  | 'parsed'
  | 'needs_review'
  | 'routing'
  | 'routed'
  | 'failed';

export type DocumentType = 'WO' | 'BOL' | 'WEIGH' | 'BRIX' | 'EXTRACTION';

// Each document type's subfolder under a client's Dropbox folder -- matching
// the folders that actually exist in Dropbox exactly. Routing creates a
// missing doc-type/year folder on demand, so this list (mirrored as an
// allowlist in route-document) is also the set of folders the app is allowed
// to create. Add a new type here only once its folder convention is real.
// The old catch-all "OTHER" type ("Other Docs") was removed 2026-09-25: it
// came from the original draft spec, never matched a real Dropbox folder, and
// its invented action list attracted misfiled documents.
export const DOC_TYPE_FOLDERS: Record<DocumentType, string> = {
  WO: 'Work Order Scans',
  BOL: 'BOLs',
  WEIGH: 'Weigh Tag Scans',
  BRIX: 'Daily Brix & Temp',
  EXTRACTION: 'Daily Extractions',
};

export const isDocumentType = (value: string | null | undefined): value is DocumentType =>
  !!value && Object.prototype.hasOwnProperty.call(DOC_TYPE_FOLDERS, value);

// BOL actions for supply deliveries (packing slips / invoices). These are
// filed by client code + what arrived ("2026.09.25 CB Yeast In.pdf") -- a
// lot code handwritten on the slip is only there to identify the client, and
// never goes in the filename, so there's no Lot ID to require.
export const SUPPLY_BOL_ACTIONS = new Set([
  'GlassIn',
  'CorkIn',
  'LabelsIn',
  'BarrelsIn',
  'BarrelsOut',
  'AdditivesIn',
  'YeastIn',
  'BacteriaIn',
]);

export const documentUsesLotId = (docType: string | null | undefined, action: string | null | undefined) =>
  !(docType === 'BOL' && !!action && SUPPLY_BOL_ACTIONS.has(action));

// Routed filename: "YYYY.MM.DD <Lot ID or client code> <Action>.pdf". Uses
// the lot ID when there's exactly one (lot codes already start with the
// client code, e.g. "PD24CSTT"), otherwise the client code -- for
// multi-lot documents and for document types that don't file by lot.
// Shared by the review sheet's destination preview and useRouteDocument so
// the preview can never disagree with what actually gets written.
export function buildRoutedFilename(opts: {
  date: Date;
  lotId: string;
  clientCode: string;
  action: string;
  usesLotId: boolean;
}): string {
  const { date, lotId, clientCode, action, usesLotId } = opts;
  const dateStr = `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
  const hasMultipleLots = /[,;&/]/.test(lotId);
  const identifierStr = usesLotId && lotId && !hasMultipleLots ? lotId : clientCode;
  // BOL actions are camelCase tokens ("AdditivesIn") -- space them out to
  // match what the Action dropdown shows ("Additives In"). WO actions are
  // already real, spaced glossary names ("H2O Add", "Rack & Return") and must
  // pass through untouched: splitting at digit->capital boundaries turned
  // "H2O Add" into "H2 O Add" (2026-07-23 to 2026-09-29).
  const actionStr = /\s/.test(action) ? action : action.replace(/([a-z])([A-Z])/g, '$1 $2');
  return `${dateStr} ${identifierStr} ${actionStr}.pdf`;
}

// Single-purpose doc types with no action subtypes — an "Action" field/badge
// should never be treated as missing for these (kept in sync with
// DOC_TYPE_ACTIONS in DocumentReviewSheet.tsx).
export const DOC_TYPES_WITHOUT_ACTION = new Set<DocumentType>(['WEIGH', 'EXTRACTION']);

export interface DocumentParseResult {
  id: string;
  org_id: string;
  document_id: string;
  doc_type: DocumentType | null;
  parsed_date: string | null;
  parsed_client_id: string | null;
  parsed_client_name: string | null;
  parsed_client_code: string | null;
  parsed_lot_id: string | null;
  parsed_action: string | null;
  confidence_score: number | null;
  confidence_breakdown: Record<string, number>;
  raw_ocr_text: string | null;
  parser_version: string | null;
  llm_enhanced: boolean;
  created_at: string;
}

export interface DocumentRoute {
  id: string;
  org_id: string;
  document_id: string;
  final_filename: string;
  final_dropbox_path: string;
  final_dropbox_file_id: string | null;
  dropbox_share_url: string | null;
  client_id: string | null;
  approved_by: string | null;
  approved_at: string | null;
  auto_routed: boolean;
  created_at: string;
}

export interface ScannedDocument {
  id: string;
  org_id: string;
  facility_id: string; // Facility this scan was detected under (by inbox), overridable in the review sheet
  original_filename: string;
  inbox_path: string;
  dropbox_file_id: string | null;
  detected_at: string;
  file_modified_at: string | null;
  file_size_bytes: number | null;
  status: ScannedDocumentStatus;
  error_message: string | null;
  retry_count: number;
  created_at: string;
  updated_at: string;
  // Joined relations
  parse_result?: DocumentParseResult | null;
  route?: DocumentRoute | null;
  client?: { id: string; name: string; code: string | null } | null;
}

export interface DocumentStats {
  total: number;
  detected: number;
  parsing: number;
  parsed: number;
  needs_review: number;
  routing: number;
  routed: number;
  failed: number;
  routed_today: number;
  routed_week: number;
  routed_month: number;
  routed_quarter: number;
}

export interface RouteDocumentInput {
  document_id: string;
  doc_type: DocumentType;
  date: string;
  client_id: string;
  lot_id: string;
  action: string;
  dropbox_folder: string; // The Dropbox folder name (e.g., "GWH - Alma & Cattleya")
  facility_id: string; // The facility (Location) selected in the review sheet
}

export interface DocumentProcessLog {
  id: string;
  org_id: string;
  document_id: string;
  event: string;
  details: {
    // OCR-related details
    ocr_model?: string;
    extraction_model?: string;
    extraction_mode?: 'text_only' | 'pdf_fallback';
    text_length?: number;
    gemini_usage?: {
      promptTokens: number;
      completionTokens: number;
      totalTokens: number;
    };
    claude_usage?: {
      input_tokens: number;
      output_tokens: number;
    };
    // General details
    filename?: string;
    confidence_score?: number;
    confidence_breakdown?: Record<string, number | string | null>;
    extracted?: Record<string, string | null>;
    error?: string;
    [key: string]: any;
  };
  occurred_at: string;
}

// ============================================================================
// Queries
// ============================================================================

/**
 * Fetch all documents with optional status filter
 */
export function useDocuments(status?: ScannedDocumentStatus) {
  return useQuery({
    queryKey: ["scanned-documents", status],
    queryFn: async () => {
      let query = supabase
        .from("scanned_document")
        .select(`
          *,
          parse_result:document_parse_result(*),
          route:document_route(
            *,
            client:client(id, name, code)
          )
        `)
        .order("detected_at", { ascending: false });

      if (status) {
        query = query.eq("status", status);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Transform the data to flatten parse_result array to single object
      // Sort parse_results by created_at DESC to get the latest one (important for re-parsing)
      return (data || []).map((doc: any) => {
        const sortedParseResults = doc.parse_result?.sort(
          (a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        return {
          ...doc,
          parse_result: sortedParseResults?.[0] || null,
          route: doc.route?.[0] || null,
          client: doc.route?.[0]?.client || null,
        };
      }) as ScannedDocument[];
    },
  });
}

/**
 * Fetch all active documents (in-progress + needs review)
 * Shows everything that isn't routed or failed
 *
 * Polls every 10s (matches useInProgressDocuments) since documents in this
 * list transition status server-side (detected -> parsing -> needs_review)
 * without any user action in this tab to trigger a refetch — scanned_document
 * isn't a Realtime-enabled table, so without polling the queue can sit on a
 * stale "Parsing" badge long after the backend has actually finished.
 */
export function useNeedsReviewDocuments() {
  return useQuery({
    queryKey: ["scanned-documents", "needs-review-all"],
    refetchInterval: 10000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("scanned_document")
        .select(`
          *,
          parse_result:document_parse_result(*),
          route:document_route(
            *,
            client:client(id, name, code)
          )
        `)
        .in("status", ["detected", "parsing", "parsed", "needs_review", "routing"])
        .order("detected_at", { ascending: false });

      if (error) throw error;

      // Transform the data to flatten parse_result array to single object
      // Sort parse_results by created_at DESC to get the latest one (important for re-parsing)
      return (data || []).map((doc: any) => {
        const sortedParseResults = doc.parse_result?.sort(
          (a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        return {
          ...doc,
          parse_result: sortedParseResults?.[0] || null,
          route: doc.route?.[0] || null,
          client: doc.route?.[0]?.client || null,
        };
      }) as ScannedDocument[];
    },
  });
}

/**
 * Fetch all routed documents (status = 'routed')
 */
export function useRoutedDocuments(dateFilter?: { startDate?: Date; endDate?: Date }) {
  const startISO = dateFilter?.startDate?.toISOString() ?? null;
  const endISO = dateFilter?.endDate?.toISOString() ?? null;

  return useQuery({
    queryKey: ["scanned-documents", "routed", startISO, endISO],
    queryFn: async () => {
      const buildQuery = () => {
        let query = supabase
          .from("scanned_document")
          .select(`
            *,
            parse_result:document_parse_result(*),
            route:document_route(
              *,
              client:client(id, name, code)
            )
          `)
          .eq("status", "routed");

        if (startISO) query = query.gte("updated_at", startISO);
        if (endISO) query = query.lte("updated_at", endISO);

        // id as a tiebreaker keeps page boundaries stable
        return query.order("updated_at", { ascending: false }).order("id");
      };

      // Fetch in pages: the API returns at most 1,000 rows per request, so a
      // single request silently truncated long periods (e.g. a busy quarter).
      const PAGE_ROWS = 1000;
      const data: any[] = [];
      for (let from = 0; ; from += PAGE_ROWS) {
        const { data: page, error } = await buildQuery().range(from, from + PAGE_ROWS - 1);
        if (error) throw error;
        data.push(...(page || []));
        if (!page || page.length < PAGE_ROWS) break;
      }

      return data.map((doc: any) => {
        const sortedParseResults = doc.parse_result?.sort(
          (a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        return {
          ...doc,
          parse_result: sortedParseResults?.[0] || null,
          route: doc.route?.[0] || null,
          client: doc.route?.[0]?.client || null,
        };
      }) as ScannedDocument[];
    },
  });
}

/** @deprecated Use useRoutedDocuments instead */
export const useRoutedTodayDocuments = useRoutedDocuments;

/**
 * Fetch a single document by ID with all related data
 */
export function useDocument(documentId: string) {
  return useQuery({
    queryKey: ["scanned-document", documentId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("scanned_document")
        .select(`
          *,
          parse_result:document_parse_result(*),
          route:document_route(
            *,
            client:client(id, name, code)
          )
        `)
        .eq("id", documentId)
        .single();

      if (error) throw error;

      // Sort parse_results by created_at DESC to get the latest one (important for re-parsing)
      const sortedParseResults = data.parse_result?.sort(
        (a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      return {
        ...data,
        parse_result: sortedParseResults?.[0] || null,
        route: data.route?.[0] || null,
        client: data.route?.[0]?.client || null,
      } as ScannedDocument;
    },
    enabled: !!documentId,
  });
}

/**
 * Fetch document statistics for dashboard
 */
export function useDocumentStats() {
  return useQuery({
    queryKey: ["document-stats"],
    queryFn: async () => {
      // Server-side counts (head requests, no rows transferred). This used to
      // download every scanned_document row and count in the browser, which
      // silently capped every number on the Documents page at the API's
      // 1,000-row response limit once total volume passed 1,000.
      const countQuery = () =>
        supabase.from("scanned_document").select("id", { count: "exact", head: true });
      const count = async (
        build: (q: ReturnType<typeof countQuery>) => ReturnType<typeof countQuery>
      ) => {
        const { count, error } = await build(countQuery());
        if (error) throw error;
        return count ?? 0;
      };

      // Time boundaries for routed document counts
      const now = new Date();
      const todayStart = new Date(now);
      todayStart.setHours(0, 0, 0, 0);

      const weekStart = new Date(todayStart);
      weekStart.setDate(weekStart.getDate() - weekStart.getDay()); // Sunday

      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

      const quarterMonth = Math.floor(now.getMonth() / 3) * 3;
      const quarterStart = new Date(now.getFullYear(), quarterMonth, 1);

      const statuses: ScannedDocumentStatus[] = [
        "detected", "parsing", "parsed", "needs_review", "routing", "routed", "failed",
      ];
      const routedSince = (start: Date) =>
        count((q) => q.eq("status", "routed").gte("updated_at", start.toISOString()));

      const [total, statusCounts, routedToday, routedWeek, routedMonth, routedQuarter] = await Promise.all([
        count((q) => q),
        Promise.all(statuses.map((s) => count((q) => q.eq("status", s)))),
        routedSince(todayStart),
        routedSince(weekStart),
        routedSince(monthStart),
        routedSince(quarterStart),
      ]);
      const counts = Object.fromEntries(statuses.map((s, i) => [s, statusCounts[i]]));

      return {
        total,
        detected: counts.detected,
        parsing: counts.parsing,
        parsed: counts.parsed,
        needs_review: counts.needs_review,
        routing: counts.routing,
        routed: counts.routed,
        failed: counts.failed,
        routed_today: routedToday,
        routed_week: routedWeek,
        routed_month: routedMonth,
        routed_quarter: routedQuarter,
      } as DocumentStats;
    },
  });
}

/**
 * Recovery: advance documents stuck in 'detected' or 'parsing' that already have parse results.
 * This handles cases where the parse-document edge function created results but the
 * status update was lost (e.g. due to timeout in the fire-and-forget pattern).
 */
export function useRecoverStaleDocuments() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      // Only consider a document stuck if it hasn't been touched recently.
      // Without this, a document that's genuinely mid-parse (Gemini + Claude
      // calls take 15-45+ seconds) but happens to have an older parse_result
      // row from a prior attempt would get prematurely advanced to
      // needs_review with stale data, right before the active parse finishes
      // and overwrites it anyway. 2 minutes matches the "if longer than 2
      // minutes, restart" guidance already shown in DocumentReviewSheet.tsx.
      const STALE_AFTER_MS = 2 * 60 * 1000;
      const staleBefore = new Date(Date.now() - STALE_AFTER_MS).toISOString();

      // Find documents stuck in 'detected' or 'parsing' that have parse results
      const { data: stuckDocs, error: queryError } = await supabase
        .from("scanned_document")
        .select("id, status, updated_at, parse_result:document_parse_result(id)")
        .in("status", ["detected", "parsing"])
        .lt("updated_at", staleBefore);

      if (queryError) throw queryError;

      // Filter to only docs that have at least one parse result
      const docsWithResults = (stuckDocs || []).filter(
        (doc: any) => doc.parse_result && doc.parse_result.length > 0
      );

      if (docsWithResults.length === 0) return { advanced: 0 };

      const ids = docsWithResults.map((d: any) => d.id);

      // Advance them to needs_review
      const { error: updateError } = await supabase
        .from("scanned_document")
        .update({ status: "needs_review" })
        .in("id", ids);

      if (updateError) throw updateError;

      return { advanced: ids.length };
    },
    onSuccess: (result) => {
      if (result.advanced > 0) {
        queryClient.invalidateQueries({ queryKey: ["scanned-documents"] });
        queryClient.invalidateQueries({ queryKey: ["document-stats"] });
      }
    },
  });
}

/**
 * Fetch processing logs for a document
 * Shows the AI processing pipeline events (OCR, extraction, etc.)
 */
export function useDocumentProcessLogs(documentId: string | null) {
  return useQuery({
    queryKey: ["document-process-logs", documentId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("document_process_log")
        .select("*")
        .eq("document_id", documentId!)
        .order("occurred_at", { ascending: true });

      if (error) throw error;
      return data as DocumentProcessLog[];
    },
    enabled: !!documentId,
  });
}

/**
 * Fetch documents currently in progress (detected, parsing, routing)
 * Auto-polls every 10 seconds since these are actively transitioning
 */
export function useInProgressDocuments() {
  return useQuery({
    queryKey: ["scanned-documents", "in-progress"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("scanned_document")
        .select(`
          *,
          parse_result:document_parse_result(*),
          route:document_route(
            *,
            client:client(id, name, code)
          )
        `)
        .in("status", ["detected", "parsing", "routing"])
        .order("detected_at", { ascending: false });

      if (error) throw error;

      return (data || []).map((doc: any) => {
        const sortedParseResults = doc.parse_result?.sort(
          (a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        return {
          ...doc,
          parse_result: sortedParseResults?.[0] || null,
          route: doc.route?.[0] || null,
          client: doc.route?.[0]?.client || null,
        };
      }) as ScannedDocument[];
    },
    refetchInterval: 10000,
  });
}

/**
 * Fetch failed documents
 */
export function useFailedDocuments() {
  return useQuery({
    queryKey: ["scanned-documents", "failed"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("scanned_document")
        .select(`
          *,
          parse_result:document_parse_result(*),
          route:document_route(
            *,
            client:client(id, name, code)
          )
        `)
        .eq("status", "failed")
        .order("detected_at", { ascending: false });

      if (error) throw error;

      return (data || []).map((doc: any) => {
        const sortedParseResults = doc.parse_result?.sort(
          (a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        return {
          ...doc,
          parse_result: sortedParseResults?.[0] || null,
          route: doc.route?.[0] || null,
          client: doc.route?.[0]?.client || null,
        };
      }) as ScannedDocument[];
    },
  });
}

// ============================================================================
// Mutations
// ============================================================================

/**
 * Route a document with corrected metadata
 */
export function useRouteDocument() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (input: RouteDocumentInput) => {
      // Get client info for filename generation
      const { data: client, error: clientError } = await supabase
        .from("client")
        .select("name, code")
        .eq("id", input.client_id)
        .single();

      if (clientError) {
        console.error('[RouteDocument] client query failed:', clientError);
        throw new Error(`Failed to get client: ${clientError.message}`);
      }

      // Parse date string as local time to avoid timezone shift
      const [year, month, day] = input.date.split('-').map(Number);
      const date = new Date(year, month - 1, day); // month is 0-indexed

      const finalFilename = buildRoutedFilename({
        date,
        lotId: input.lot_id,
        clientCode: client.code || client.name.substring(0, 2).toUpperCase(),
        action: input.action,
        usesLotId: documentUsesLotId(input.doc_type, input.action),
      });

      // Determine subfolder based on document type (matching existing Dropbox folders exactly)
      if (!isDocumentType(input.doc_type)) {
        throw new Error(`Unknown document type: ${input.doc_type}`);
      }
      const docTypeFolder = DOC_TYPE_FOLDERS[input.doc_type];

      // Use the selected dropbox_folder from the form (e.g., "GWH - Alma & Cattleya")
      const finalDropboxPath = `/GWH Clients/${input.dropbox_folder}/${docTypeFolder}/${date.getFullYear()}/${finalFilename}`;

      // Call Edge Function to actually move the file in Dropbox. The
      // document_route record is created by the edge function itself, only
      // after Dropbox confirms the move — previously it was inserted here
      // first, so a failed move left an orphaned row pointing at a path the
      // file never actually reached.
      const { data: moveResult, error: moveError } = await supabase.functions.invoke('route-document', {
        body: {
          document_id: input.document_id,
          final_dropbox_path: finalDropboxPath,
          final_filename: finalFilename,
          client_id: input.client_id,
          facility_id: input.facility_id,
        },
      });

      if (moveError) throw moveError;
      if (!moveResult.success) throw new Error(moveResult.error || 'Failed to move file in Dropbox');

      return moveResult;
    },
    // DocumentReviewSheet closes as soon as routing starts, so show the row
    // as "Routing" immediately instead of leaving it looking untouched until
    // the next 10s refetch picks up the server-side status change.
    onMutate: async (input: RouteDocumentInput) => {
      const needsReviewKey = ["scanned-documents", "needs-review-all"];
      await queryClient.cancelQueries({ queryKey: needsReviewKey });
      queryClient.setQueryData<ScannedDocument[]>(needsReviewKey, (docs) =>
        docs?.map((doc) =>
          doc.id === input.document_id ? { ...doc, status: "routing" } : doc
        )
      );
    },
    onSuccess: () => {
      toast({
        title: "Document routed",
        description: "The document has been successfully routed to the client folder.",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["scanned-documents"] });
      queryClient.invalidateQueries({ queryKey: ["document-stats"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error routing document",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * Retry/restart processing a failed or stuck document
 * Resets status to 'detected' so it can be parsed again
 */
export function useRetryDocument() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (documentId: string) => {
      // Atomic increment (avoids the lost-update race of read-then-write
      // under concurrent retries) — see increment_document_retry_count().
      const { data: retryCount, error } = await supabase.rpc(
        "increment_document_retry_count",
        { p_document_id: documentId }
      );

      if (error) throw error;
      return { retry_count: retryCount };
    },
    onSuccess: (_, documentId) => {
      queryClient.invalidateQueries({ queryKey: ["scanned-documents"] });
      queryClient.invalidateQueries({ queryKey: ["scanned-document", documentId] });
      queryClient.invalidateQueries({ queryKey: ["document-stats"] });
      toast({
        title: "Retry queued",
        description: "The document has been queued for reprocessing.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error retrying document",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * Bulk retry multiple failed documents at once
 * Uses Promise.allSettled to handle partial failures
 */
export function useBulkRetryDocuments() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (documentIds: string[]) => {
      const results = await Promise.allSettled(
        documentIds.map(async (id) => {
          const { data: retryCount, error } = await supabase.rpc(
            "increment_document_retry_count",
            { p_document_id: id }
          );

          if (error) throw error;
          return { retry_count: retryCount };
        })
      );

      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;
      return { succeeded, failed, total: documentIds.length };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["scanned-documents"] });
      queryClient.invalidateQueries({ queryKey: ["document-stats"] });
      toast({
        title: "Bulk retry queued",
        description: result.failed > 0
          ? `${result.succeeded} of ${result.total} documents queued for reprocessing. ${result.failed} failed.`
          : `${result.succeeded} document${result.succeeded !== 1 ? 's' : ''} queued for reprocessing.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error retrying documents",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * Response from poll-dropbox-inbox function
 */
interface PollInboxResponse {
  success: boolean;
  skipped?: boolean;
  message?: string;
  cooldown_remaining?: number;
  results?: {
    scanned: number;
    new: number;
    created: number;
    parsed: number;
    skipped: number;
    errors: string[];
  };
  error?: string;
}

/**
 * Trigger a poll of the Dropbox inbox folder
 */
export function usePollDropboxInbox() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (): Promise<PollInboxResponse> => {
      const { data, error } = await supabase.functions.invoke('poll-dropbox-inbox');

      if (error) throw error;
      if (!data.success && !data.skipped) throw new Error(data.error || 'Failed to poll inbox');

      return data as PollInboxResponse;
    },
    onSuccess: (data) => {
      // Handle cooldown/skipped case
      if (data.skipped) {
        toast({
          title: "Please wait",
          description: data.message || `Cooldown active. Please wait ${data.cooldown_remaining || 30} seconds.`,
        });
        return;
      }

      const results = data.results!;
      queryClient.invalidateQueries({ queryKey: ["scanned-documents"] });
      queryClient.invalidateQueries({ queryKey: ["document-stats"] });
      queryClient.invalidateQueries({ queryKey: ["last-scan-time"] });

      if (results.created > 0) {
        toast({
          title: "New documents found",
          description: `Found ${results.created} new document${results.created > 1 ? 's' : ''} in the inbox`,
        });
      } else if (results.skipped > 0) {
        toast({
          title: "Inbox checked",
          description: `No new documents. ${results.skipped} duplicate${results.skipped > 1 ? 's' : ''} skipped.`,
        });
      } else {
        toast({
          title: "Inbox checked",
          description: "No new documents found",
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Error checking inbox",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * Fetch the last time the Dropbox inbox was scanned
 */
export function useLastScanTime() {
  return useQuery({
    queryKey: ["last-scan-time"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("poll_lock")
        .select("locked_at")
        .eq("lock_type", "dropbox_inbox")
        .maybeSingle();

      if (error) throw error;
      return data?.locked_at as string | null;
    },
  });
}

/**
 * Delete a document from the queue
 */
export function useDeleteDocument() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (documentId: string) => {
      const { error } = await supabase
        .from("scanned_document")
        .delete()
        .eq("id", documentId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scanned-documents"] });
      queryClient.invalidateQueries({ queryKey: ["document-stats"] });
      toast({
        title: "Document deleted",
        description: "The document has been removed from the queue.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error deleting document",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * Update parse result with corrected metadata
 */
export function useUpdateParseResult() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      documentId,
      updates
    }: {
      documentId: string;
      updates: Partial<DocumentParseResult>;
    }) => {
      const { data, error } = await supabase
        .from("document_parse_result")
        .update(updates)
        .eq("document_id", documentId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["scanned-document", variables.documentId] });
      toast({
        title: "Parse result updated",
        description: "The document metadata has been updated.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating metadata",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * Get a blob URL for previewing a document PDF
 * Fetches the PDF through our Edge Function (which proxies from Dropbox)
 */
export function useDocumentPreview(documentId: string | null) {
  return useQuery({
    queryKey: ["document-preview", documentId],
    queryFn: async () => {
      if (!documentId) return null;

      // Get the PDF data from our Edge Function (proxied from Dropbox)
      const { data, error } = await supabase.functions.invoke('get-document-preview', {
        body: { document_id: documentId },
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error || 'Failed to get preview');
      if (!data.pdf_data) return null;

      // Convert base64 to blob
      const binaryString = atob(data.pdf_data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);

      return blobUrl;
    },
    enabled: !!documentId,
    staleTime: 1000 * 60 * 30, // Cache for 30 minutes
    retry: false,
  });
}

export interface ParseDocumentResult {
  success: boolean;
  error?: string;
  error_code?: string;
  status?: string;
  confidence_score?: number;
}

/**
 * Trigger OCR parsing for a document
 */
export function useParseDocument() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (documentId: string): Promise<ParseDocumentResult> => {
      const { data, error } = await supabase.functions.invoke('parse-document', {
        body: { document_id: documentId },
      });

      if (error) throw error;

      // Return the data even if not successful - we'll handle FILE_NOT_FOUND specially
      return data as ParseDocumentResult;
    },
    onSuccess: (result, documentId) => {
      queryClient.invalidateQueries({ queryKey: ["scanned-documents"] });
      queryClient.invalidateQueries({ queryKey: ["scanned-document", documentId] });
      queryClient.invalidateQueries({ queryKey: ["document-stats"] });

      // Handle file not found specially
      if (result.error_code === 'FILE_NOT_FOUND') {
        toast({
          title: "File not found",
          description: "This file no longer exists in Dropbox. It may have been renamed, moved, or deleted.",
          variant: "destructive",
        });
        return;
      }

      if (result.error_code === 'PARSE_IN_PROGRESS') {
        toast({
          title: "Parse already running",
          description: "This document is already being processed. Please wait for it to finish.",
        });
        return;
      }

      if (result.error_code === 'RESCAN_LIMIT_EXCEEDED') {
        toast({
          title: "Rescan limit reached",
          description: result.error || "This document has reached its parse/rescan limit.",
          variant: "destructive",
        });
        return;
      }

      if (!result.success) {
        toast({
          title: "Error parsing document",
          description: result.error || 'Failed to parse document',
          variant: "destructive",
        });
        return;
      }

      const confidencePercent = Math.round((result.confidence_score || 0) * 100);
      toast({
        title: result.status === 'parsed' ? "Document parsed" : "Needs review",
        description: result.status === 'parsed'
          ? `Parsed with ${confidencePercent}% confidence`
          : `Low confidence (${confidencePercent}%). Please review and correct the metadata.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error parsing document",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * Check if a document still exists in Dropbox
 * Returns { exists: boolean, error?: string }
 */
export function useValidateDocumentExists() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (documentId: string) => {
      const { data, error } = await supabase.functions.invoke('validate-document-exists', {
        body: { document_id: documentId },
      });

      if (error) throw error;
      return data as { exists: boolean; error?: string; path?: string };
    },
    onError: (error: Error) => {
      toast({
        title: "Error validating document",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * Remove documents that no longer exist in Dropbox
 * Can remove a single document or multiple documents
 */
export function useRemoveMissingDocuments() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (documentIds: string | string[]) => {
      const ids = Array.isArray(documentIds) ? documentIds : [documentIds];

      // Delete parse results first (foreign key constraint)
      const { error: parseError } = await supabase
        .from("document_parse_result")
        .delete()
        .in("document_id", ids);

      if (parseError) throw parseError;

      // Delete routes
      const { error: routeError } = await supabase
        .from("document_route")
        .delete()
        .in("document_id", ids);

      if (routeError) throw routeError;

      // Delete the documents
      const { error } = await supabase
        .from("scanned_document")
        .delete()
        .in("id", ids);

      if (error) throw error;

      return { removed: ids.length };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["scanned-documents"] });
      queryClient.invalidateQueries({ queryKey: ["document-stats"] });
      toast({
        title: "Documents removed",
        description: `Removed ${result.removed} document${result.removed > 1 ? 's' : ''} that no longer exist in Dropbox.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error removing documents",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * Scan and cleanup documents that no longer exist in Dropbox
 * Returns list of removed document IDs
 */
export function useCleanupMissingDocuments() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('cleanup-missing-documents');

      if (error) throw error;
      if (!data.success) throw new Error(data.error || 'Failed to cleanup documents');

      return data as {
        scanned: number;
        removed: number;
        removedIds: string[];
        errors: string[];
      };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["scanned-documents"] });
      queryClient.invalidateQueries({ queryKey: ["document-stats"] });

      if (result.removed > 0) {
        toast({
          title: "Cleanup complete",
          description: `Scanned ${result.scanned} documents. Removed ${result.removed} that no longer exist in Dropbox.`,
        });
      } else {
        toast({
          title: "Cleanup complete",
          description: `Scanned ${result.scanned} documents. All documents still exist in Dropbox.`,
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Error during cleanup",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * Helper to check if an error indicates file not found in Dropbox
 */
export function isDropboxNotFoundError(error: unknown): boolean {
  if (!error) return false;
  const message = error instanceof Error ? error.message : String(error);
  return message.includes('path_not_found') ||
         message.includes('not_found') ||
         message.includes('409') ||
         message.includes('File not found');
}
